import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';

import {
  mapAttributions,
  basemapDefinitions,
  defaultLayerVisibility,
  defaultBasemap,
  layerDefinitions,
  nextBasemap,
  type BasemapId,
  type LayerGroupId,
} from '../domain/layers.ts';
import {
  createLocalization,
  languageChoices,
  type Localization,
  type MessageId,
  type SupportedLocale,
} from '../localization/locale.ts';
import type { LocaleUrlState } from '../localization/url-state.ts';
import type { CameraNavigation } from './camera-navigation.ts';
import type { TrailSelectionUrlState } from './browser-trail-selection.ts';
import { TrailStatus } from './TrailStatus.tsx';
import type { TrailStatusController } from './trail-status.ts';
import type {
  GeolocationFailureReason,
  MapCapabilities,
  MapEngineAdapter,
  MapEvent,
  MapMountOptions,
  MapSession,
} from '../map/contracts.ts';
import type { ProviderId } from '../domain/errors.ts';

type Panel = 'information' | 'layers';
type MapMountStatus = 'mounting' | 'ready' | 'error';

interface ApplicationMapMount {
  readonly adapter: MapEngineAdapter;
  readonly options: MapMountOptions;
}

interface AppShellProps {
  readonly locale?: SupportedLocale;
  readonly localeUrlState?: LocaleUrlState;
  readonly cameraNavigation?: CameraNavigation;
  readonly trailSelectionUrlState?: TrailSelectionUrlState;
  readonly trailStatusController?: TrailStatusController;
  readonly startupFailure?: boolean;
  readonly map?: ApplicationMapMount | undefined;
  readonly onMapStatusChange?: (status: MapMountStatus) => void;
}

const legacyDifficultyLegend = Object.freeze([
  { className: 'difficulty-3', messageId: 'legend.difficulty.easy' },
  { className: 'difficulty-10', messageId: 'legend.difficulty.professional' },
  { className: 'difficulty-4', messageId: 'legend.difficulty.moderate' },
  { className: 'difficulty-7', messageId: 'legend.difficulty.gravel' },
  { className: 'difficulty-9', messageId: 'legend.difficulty.expert' },
  { className: 'difficulty-1', messageId: 'legend.difficulty.road' },
] as const satisfies readonly { readonly className: string; readonly messageId: MessageId }[]);

const geolocationFailureMessageIds = {
  denied: 'geolocation.denied',
  insecure: 'geolocation.insecure',
  timeout: 'geolocation.timeout',
  unavailable: 'geolocation.unavailable',
  unsupported: 'geolocation.unsupported',
} as const satisfies Readonly<Record<GeolocationFailureReason, MessageId>>;

const providerMessageIds = {
  analytics: 'providers.analytics',
  esri: 'providers.esri',
  openfreemap: 'providers.openfreemap',
  swisstopo: 'providers.swisstopo',
  terrain: 'providers.terrain',
  'trail-status': 'providers.trailStatus',
  zugerwald: 'providers.zugerwald',
} as const satisfies Readonly<Record<ProviderId, MessageId>>;

function MapHost({
  map,
  onEvent,
  onSessionChange,
  localization,
  onStatusChange,
  startupFailure,
}: {
  readonly localization: Localization;
  readonly map: ApplicationMapMount | undefined;
  readonly onEvent: (event: MapEvent) => void;
  readonly onSessionChange: (session: MapSession | undefined) => void;
  readonly onStatusChange: ((status: MapMountStatus) => void) | undefined;
  readonly startupFailure: boolean;
}): ReactElement {
  const host = useRef<HTMLDivElement>(null);
  const desiredLocale = useRef(localization.locale);
  const sessionReference = useRef<MapSession | undefined>(undefined);
  const sessionLocale = useRef(localization.locale);
  const [status, setStatus] = useState<MapMountStatus>('mounting');
  desiredLocale.current = localization.locale;

  useEffect(() => {
    if (!startupFailure) return;
    setStatus('error');
    onStatusChange?.('error');
  }, [onStatusChange, startupFailure]);

  function applyLocale(session: MapSession, locale: SupportedLocale): void {
    if (sessionLocale.current === locale) return;
    sessionLocale.current = locale;
    void session.execute({ type: 'set-locale', locale }).catch(() => undefined);
  }

  useEffect(() => {
    if (map === undefined || host.current === null) return;
    let disposed = false;
    let session: MapSession | undefined;
    let unsubscribe: (() => void) | undefined;
    function resizeMap(): void {
      const activeSession = sessionReference.current;
      if (activeSession !== undefined) {
        void activeSession.execute({ type: 'resize' }).catch(() => undefined);
      }
    }
    window.addEventListener('resize', resizeMap);
    window.addEventListener('orientationchange', resizeMap);
    setStatus('mounting');
    onStatusChange?.('mounting');

    void map.adapter
      .mount(host.current, map.options)
      .then((mountedSession) => {
        if (disposed) {
          mountedSession.destroy();
          return;
        }
        session = mountedSession;
        sessionReference.current = mountedSession;
        onSessionChange(mountedSession);
        sessionLocale.current = map.options.locale;
        applyLocale(mountedSession, desiredLocale.current);
        unsubscribe = session.subscribe((event) => {
          onEvent(event);
          if (event.type === 'ready') {
            setStatus('ready');
            onStatusChange?.('ready');
          } else if (event.type === 'error' && event.error.severity === 'fatal') {
            setStatus('error');
            onStatusChange?.('error');
          }
        });
      })
      .catch(() => {
        if (disposed) return;
        setStatus('error');
        onStatusChange?.('error');
      });

    return () => {
      disposed = true;
      unsubscribe?.();
      session?.destroy();
      if (sessionReference.current === session) {
        sessionReference.current = undefined;
        onSessionChange(undefined);
      }
      window.removeEventListener('resize', resizeMap);
      window.removeEventListener('orientationchange', resizeMap);
    };
  }, [map, onEvent, onSessionChange, onStatusChange]);

  useEffect(() => {
    const session = sessionReference.current;
    if (session !== undefined) applyLocale(session, localization.locale);
  }, [localization.locale]);

  const messageId: MessageId =
    status === 'error'
      ? 'map.status.error'
      : map === undefined
        ? 'map.status.notMounted'
        : status === 'ready'
          ? 'map.status.ready'
          : 'map.status.mounting';

  return (
    <div ref={host} className="map-host" data-map-status={status}>
      <p>{localization.message(messageId)}</p>
    </div>
  );
}

export function AppShell({
  locale = 'de',
  localeUrlState,
  cameraNavigation,
  trailSelectionUrlState,
  trailStatusController,
  startupFailure = false,
  map,
  onMapStatusChange,
}: AppShellProps): ReactElement {
  const [activePanel, setActivePanel] = useState<Panel | null>(null);
  const [activeLocale, setActiveLocale] = useState(locale);
  const [liveMessage, setLiveMessage] = useState('');
  const [mapStatus, setMapStatus] = useState<MapMountStatus>('mounting');
  const [layerVisibility, setLayerVisibility] = useState(defaultLayerVisibility);
  const [activeBasemap, setActiveBasemap] = useState<BasemapId>(defaultBasemap().id);
  const [mapPitch, setMapPitch] = useState(0);
  const [mapCapabilities, setMapCapabilities] = useState<MapCapabilities | undefined>();
  const [mapErrorMessage, setMapErrorMessage] = useState('');
  const [mapRetryAvailable, setMapRetryAvailable] = useState(false);
  const retryMapAction = useRef<(() => void) | undefined>(undefined);
  const mapSession = useRef<MapSession | undefined>(undefined);
  const disconnectCameraNavigation = useRef<(() => void) | undefined>(undefined);
  const initialTrailSelectionApplied = useRef(false);
  const layerButton = useRef<HTMLButtonElement>(null);
  const layerPanel = useRef<HTMLElement>(null);
  const informationButton = useRef<HTMLButtonElement>(null);
  const informationPanel = useRef<HTMLElement>(null);
  const localization = useMemo(() => createLocalization(activeLocale), [activeLocale]);
  const activeLocalization = useRef(localization);
  activeLocalization.current = localization;
  const message = localization.message;
  const nextBasemapDefinition = nextBasemap(activeBasemap);

  const handleMapSessionChange = useCallback(
    (session: MapSession | undefined): void => {
      disconnectCameraNavigation.current?.();
      disconnectCameraNavigation.current = undefined;
      mapSession.current = session;
      if (session === undefined) initialTrailSelectionApplied.current = false;
      if (session !== undefined) {
        disconnectCameraNavigation.current = cameraNavigation?.connect(session);
      }
    },
    [cameraNavigation],
  );

  const handleMapEvent = useCallback(
    (event: MapEvent): void => {
      if (event.type === 'ready') {
        setLayerVisibility(event.state.layerVisibility);
        setActiveBasemap(event.state.basemap);
        setMapPitch(event.state.camera.pitch);
        setMapCapabilities(event.capabilities);
        const trailSelection = trailSelectionUrlState;
        const initialTrailId = trailSelection?.initialTrailId;
        if (
          !initialTrailSelectionApplied.current &&
          trailSelection !== undefined &&
          initialTrailId !== null &&
          initialTrailId !== undefined
        ) {
          initialTrailSelectionApplied.current = true;
          const session = mapSession.current;
          if (session !== undefined) {
            void session
              .execute({
                type: 'select-trail',
                trailId: initialTrailId,
                fit: !trailSelection.hasExplicitCamera,
              })
              .catch(() => undefined);
          }
        }
      } else if (event.type === 'camera-changed') {
        setMapPitch(event.camera.pitch);
        cameraNavigation?.cameraChanged(event.camera, event.movement);
      } else if (event.type === 'presentation-changed') {
        setMapPitch((current) =>
          event.presentation === 'terrain' ? Math.max(current, Number.EPSILON) : 0,
        );
      } else if (event.type === 'capabilities-changed') {
        setMapCapabilities(event.capabilities);
      } else if (event.type === 'layer-visibility-changed') {
        setLayerVisibility((current) => ({ ...current, [event.layer]: event.visible }));
        const layer = layerDefinitions.find(({ id }) => id === event.layer);
        if (layer !== undefined) {
          const currentLocalization = activeLocalization.current;
          setLiveMessage(
            currentLocalization.message(event.visible ? 'live.layerShown' : 'live.layerHidden', {
              name: currentLocalization.message(layer.labelMessageId),
            }),
          );
        }
      } else if (event.type === 'basemap-changed') {
        setActiveBasemap(event.basemap);
        const basemap = basemapDefinitions.find(({ id }) => id === event.basemap);
        if (basemap !== undefined) {
          const currentLocalization = activeLocalization.current;
          setLiveMessage(
            currentLocalization.message('live.basemapChanged', {
              name: currentLocalization.message(basemap.labelMessageId),
            }),
          );
        }
      } else if (event.type === 'trail-selected') {
        trailSelectionUrlState?.replace(event.trailId);
        const trail = map?.options.content.selectableTrails.find(({ id }) => id === event.trailId);
        if (trail !== undefined) {
          setLiveMessage(
            activeLocalization.current.message('live.trailSelected', { name: trail.title }),
          );
        }
      } else if (event.type === 'trail-selection-cleared') {
        trailSelectionUrlState?.replace(null);
        setLiveMessage(activeLocalization.current.message('live.popupClosed'));
      } else if (event.type === 'geolocation-result') {
        setMapErrorMessage('');
        setLiveMessage(activeLocalization.current.message('live.locationReceived'));
      } else if (event.type === 'geolocation-error') {
        setMapErrorMessage(
          activeLocalization.current.message(geolocationFailureMessageIds[event.reason]),
        );
        setMapRetryAvailable(false);
        retryMapAction.current = undefined;
      } else if (event.type === 'error') {
        const providerValue = event.error.diagnostics.provider;
        const provider = providerValue as ProviderId | undefined;
        const currentLocalization = activeLocalization.current;
        setMapErrorMessage(
          provider !== undefined && provider in providerMessageIds
            ? currentLocalization.message('errors.providerUnavailable', {
                name: currentLocalization.message(providerMessageIds[provider]),
              })
            : currentLocalization.message(
                event.error.severity === 'fatal' ? 'errors.engineFatal' : 'errors.mapUnavailable',
              ),
        );
        if (event.error.retryable && provider !== undefined && provider in providerMessageIds) {
          retryMapAction.current = () => {
            const session = mapSession.current;
            if (session === undefined) return;
            void session
              .execute({ type: 'retry-provider', provider })
              .then((result) => {
                if (result.status === 'applied' || result.status === 'queued') {
                  setMapErrorMessage('');
                  setMapRetryAvailable(false);
                  retryMapAction.current = undefined;
                }
              })
              .catch(() => undefined);
          };
          setMapRetryAvailable(true);
        } else {
          retryMapAction.current = undefined;
          setMapRetryAvailable(false);
        }
      }
    },
    [cameraNavigation, map, trailSelectionUrlState],
  );

  const handleMapStatusChange = useCallback(
    (status: MapMountStatus): void => {
      setMapStatus(status);
      onMapStatusChange?.(status);
    },
    [onMapStatusChange],
  );

  useEffect(() => {
    document.documentElement.lang = activeLocale;
    document.title = message('meta.title');
    document
      .querySelector<HTMLMetaElement>('meta[name="description"]')
      ?.setAttribute('content', message('meta.description'));
  }, [activeLocale, message]);

  useEffect(() => {
    if (activePanel === null) return;
    const openedPanel = activePanel === 'layers' ? layerPanel.current : informationPanel.current;
    openedPanel?.focus();

    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      const trigger = activePanel === 'layers' ? layerButton.current : informationButton.current;
      setActivePanel(null);
      trigger?.focus();
    }

    function closeOnOutsideActivation(event: MouseEvent): void {
      if (!(event.target instanceof Node)) return;
      const panel = activePanel === 'layers' ? layerPanel.current : informationPanel.current;
      const trigger = activePanel === 'layers' ? layerButton.current : informationButton.current;
      if (
        panel?.contains(event.target) === true ||
        layerButton.current?.contains(event.target) === true ||
        informationButton.current?.contains(event.target) === true
      )
        return;
      setActivePanel(null);
      trigger?.focus();
    }

    document.addEventListener('keydown', closeOnEscape);
    document.addEventListener('click', closeOnOutsideActivation);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.removeEventListener('click', closeOnOutsideActivation);
    };
  }, [activePanel]);

  function togglePanel(panel: Panel): void {
    setActivePanel((current) => (current === panel ? null : panel));
  }

  function changeLocale(nextLocale: SupportedLocale): void {
    if (nextLocale === activeLocale) return;
    localeUrlState?.replace(nextLocale);
    setLiveMessage(localization.withLocale(nextLocale).message('live.localeChanged'));
    setActiveLocale(nextLocale);
  }

  function changeLayerVisibility(layer: LayerGroupId, visible: boolean): void {
    const session = mapSession.current;
    if (session === undefined || mapStatus !== 'ready') return;
    const layerDefinition = layerDefinitions.find(({ id }) => id === layer);
    if (layerDefinition === undefined) return;
    setMapErrorMessage('');
    setMapRetryAvailable(false);
    retryMapAction.current = undefined;
    const retry = (): void => {
      changeLayerVisibility(layer, visible);
    };
    void session
      .execute({ type: 'set-layer-visibility', layer, visible })
      .then((result) => {
        if (result.status === 'applied' || result.status === 'queued') return;
        setMapErrorMessage(
          message('errors.layerUnavailable', {
            name: message(layerDefinition.labelMessageId),
          }),
        );
        retryMapAction.current = retry;
        setMapRetryAvailable(true);
      })
      .catch(() => {
        setMapErrorMessage(
          message('errors.layerUnavailable', {
            name: message(layerDefinition.labelMessageId),
          }),
        );
        retryMapAction.current = retry;
        setMapRetryAvailable(true);
      });
  }

  function changeBasemap(): void {
    const session = mapSession.current;
    if (session === undefined || mapStatus !== 'ready') return;
    setMapErrorMessage('');
    setMapRetryAvailable(false);
    retryMapAction.current = undefined;
    void session
      .execute({ type: 'set-basemap', basemap: nextBasemapDefinition.id })
      .then((result) => {
        if (result.status === 'applied' || result.status === 'queued') return;
        setMapErrorMessage(message('errors.basemapUnavailable'));
        retryMapAction.current = changeBasemap;
        setMapRetryAvailable(true);
      })
      .catch(() => {
        setMapErrorMessage(message('errors.basemapUnavailable'));
        retryMapAction.current = changeBasemap;
        setMapRetryAvailable(true);
      });
  }

  function changePresentation(): void {
    const session = mapSession.current;
    if (session === undefined || mapStatus !== 'ready') return;
    const presentation = mapPitch > 0 ? 'flat' : 'terrain';
    void session.execute({ type: 'set-presentation', presentation }).catch(() => undefined);
  }

  const presentationMessageId = mapPitch > 0 ? 'terrain.disable' : 'terrain.enable';
  const presentationAvailable =
    mapStatus === 'ready' &&
    (mapPitch > 0 ? mapCapabilities?.flat === true : mapCapabilities?.terrain === true);

  return (
    <main className="app-shell" aria-label={message('app.label')}>
      <MapHost
        map={map}
        localization={localization}
        onEvent={handleMapEvent}
        onSessionChange={handleMapSessionChange}
        onStatusChange={handleMapStatusChange}
        startupFailure={startupFailure}
      />

      <section className="branding" data-region="branding" aria-label={message('branding.label')}>
        <div className="title-bar">
          <a href="https://mountainbikezug.ch/" target="_blank" rel="noreferrer">
            <img
              className="title-logo-mtb"
              src="https://mountainbikezug.ch/img/igmtb_logo.svg"
              alt="IG Mountainbike Zug"
            />
          </a>
        </div>
        <div className="trail-status-bar">
          <a href="https://zugerbergtrail.ch/" target="_blank" rel="noreferrer">
            <img
              className="title-logo-trail"
              src="https://zugerbergtrail.ch/wp-content/uploads/2016/02/Zugerberg-Trail-Logo-CMYK-1@05x.png"
              alt="ZugerbergTrail"
            />
          </a>
          <TrailStatus
            localization={localization}
            {...(trailStatusController === undefined ? {} : { controller: trailStatusController })}
          />
        </div>
      </section>

      <nav className="controls" data-region="controls" aria-label={message('controls.label')}>
        <button
          ref={layerButton}
          type="button"
          className="shell-control layers-control"
          data-control="layers"
          aria-controls="layers-panel"
          aria-expanded={activePanel === 'layers'}
          aria-label={message('controls.layers')}
          title={message('controls.layers')}
          onClick={() => {
            togglePanel('layers');
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <path d="M12 2 2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
        </button>
        <button
          ref={informationButton}
          type="button"
          className="shell-control information-control"
          data-control="information"
          aria-controls="information-panel"
          aria-expanded={activePanel === 'information'}
          aria-label={message('controls.information')}
          title={message('controls.information')}
          onClick={() => {
            togglePanel('information');
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
        </button>
        <button
          type="button"
          className="presentation-control map-style-control"
          data-control="presentation"
          disabled={!presentationAvailable}
          aria-label={message(presentationMessageId)}
          title={message(presentationMessageId)}
          onClick={changePresentation}
        >
          <span className="map-style-control-face" aria-hidden="true">
            {mapPitch > 0 ? '2D' : '3D'}
          </span>
        </button>
        <button
          type="button"
          className="shell-control basemap-control"
          data-control="basemap"
          disabled={mapStatus !== 'ready'}
          aria-label={message(nextBasemapDefinition.switchMessageId)}
          title={mapStatus === 'ready' ? undefined : message('controls.basemap.pendingTitle')}
          onClick={changeBasemap}
        >
          <span
            className={`basemap-thumbnail basemap-thumbnail-${nextBasemapDefinition.id}`}
            data-basemap={nextBasemapDefinition.id}
            aria-hidden="true"
          />
        </button>
      </nav>

      <section
        ref={layerPanel}
        id="layers-panel"
        className="panel"
        aria-label={message('panels.layers.label')}
        tabIndex={-1}
        hidden={activePanel !== 'layers'}
      >
        <h2>{message('panels.layers.title')}</h2>
        {mapStatus === 'ready' ? null : <p>{message('panels.layers.pending')}</p>}
        <ul className="layer-list">
          {layerDefinitions.map((layer) => (
            <li key={layer.id}>
              <label>
                <input
                  type="checkbox"
                  checked={layerVisibility[layer.id]}
                  disabled={mapStatus !== 'ready'}
                  onChange={(event) => {
                    changeLayerVisibility(layer.id, event.currentTarget.checked);
                  }}
                />
                {message(layer.labelMessageId)}
              </label>
            </li>
          ))}
        </ul>
      </section>

      <section
        ref={informationPanel}
        id="information-panel"
        className="panel"
        aria-label={message('panels.information.label')}
        tabIndex={-1}
        hidden={activePanel !== 'information'}
      >
        <div className="panel-title">{message('legend.difficulty.title')}</div>
        <div className="legend-grid">
          {legacyDifficultyLegend.map((entry) => (
            <div className="legend-item" key={entry.className}>
              <span className={`difficulty ${entry.className}`} aria-hidden="true" />
              <span className="legend-label">{message(entry.messageId)}</span>
            </div>
          ))}
        </div>
        <div className="panel-title panel-title-separated">{message('legend.zugerwald.title')}</div>
        <div className="legend-grid">
          <div className="legend-item">
            <span className="line-legend line-legend-zugerwald-bike" aria-hidden="true" />
            <span className="legend-label">{message('legend.zugerwald.bikeRoute')}</span>
          </div>
          <div className="legend-item">
            <span className="line-legend line-legend-zugerwald-road" aria-hidden="true" />
            <span className="legend-label">{message('legend.zugerwald.forestRoad')}</span>
          </div>
        </div>
        <div className="panel-title panel-title-separated">
          {message('legend.publicTransport.title')}
        </div>
        <div className="legend-grid">
          <div className="legend-item">
            <span className="line-legend line-legend-public-transport" aria-hidden="true" />
            <span className="legend-label">{message('legend.publicTransport.zugerbergbahn')}</span>
          </div>
        </div>
        <div className="legend-details">
          <div>
            {message('sources.base')}:{' '}
            <a href="https://bgs.zg.ch/app/de/texts_of_law/711.32" target="_blank" rel="noreferrer">
              {message('sources.cantonalPlan')}
            </a>
          </div>
          <div>{message('legend.plannedNotice')}</div>
          <div>
            {message('sources.trailData')}:{' '}
            <a
              href="https://www.trailforks.com/region/zug-kanton/"
              target="_blank"
              rel="noreferrer"
            >
              Trailforks
            </a>
          </div>
          <div>
            {message('sources.bikeRoutes')}:{' '}
            <a href="https://zugerwald.ch/bike" target="_blank" rel="noreferrer">
              Zugerwald
            </a>
          </div>
          <div>
            {message('sources.bikeTransport')}:{' '}
            <a
              href="https://www.zbb.ch/freizeit-oase/zugerberg-erlebnisse/biken/"
              target="_blank"
              rel="noreferrer"
            >
              Zugerbergbahn
            </a>
          </div>
          <div id="map-attribution">
            {message('sources.backgroundMap')}:{' '}
            {mapAttributions(activeBasemap)
              .filter(({ id }) => id !== 'openstreetmap')
              .map((attribution, index) => (
                <span key={attribution.id}>
                  {index > 0 ? ' · ' : null}©{' '}
                  <a href={attribution.url} target="_blank" rel="noreferrer">
                    {attribution.label}
                  </a>
                </span>
              ))}
            <br />
            {message('sources.zugerbergbahnRoute')}: ©{' '}
            {mapAttributions(activeBasemap)
              .filter(({ id }) => id === 'openstreetmap')
              .map((attribution) => (
                <a key={attribution.id} href={attribution.url} target="_blank" rel="noreferrer">
                  {message('sources.openStreetMapContributors')}
                </a>
              ))}
          </div>
          <div className="disclaimer">⚠️ {message('disclaimer.text')}</div>
        </div>
        <fieldset className="language-selector">
          <legend>{message('panels.languages.title')}</legend>
          <div className="language-options">
            {languageChoices.map((choice) => (
              <button
                key={choice.locale}
                type="button"
                aria-pressed={choice.locale === activeLocale}
                onClick={() => {
                  changeLocale(choice.locale);
                }}
              >
                {choice.label}
              </button>
            ))}
          </div>
        </fieldset>
      </section>

      <div className="visually-hidden" data-region="application-live" aria-live="polite">
        {liveMessage}
      </div>
      {mapErrorMessage === '' && mapStatus !== 'error' ? null : (
        <div className="provider-error" data-region="application-error" role="alert">
          <span>
            {mapErrorMessage === '' && mapStatus === 'error'
              ? message('errors.engineFatal')
              : mapErrorMessage}
          </span>
          {mapStatus === 'error' ? (
            <button
              type="button"
              onClick={() => {
                window.location.reload();
              }}
            >
              {message('errors.reload')}
            </button>
          ) : mapRetryAvailable ? (
            <button type="button" onClick={() => retryMapAction.current?.()}>
              {message('errors.retry')}
            </button>
          ) : null}
        </div>
      )}
    </main>
  );
}
